import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import DisciplineAnchor from '@om-fe/core/components/discipline-anchor/discipline-anchor';
import { isRpcStateReady, RpcState, RpcStateReady, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { DisciplineModel } from '@om-fe/models';
import { TournamentBreadcrumbs } from './tournament-breadcrumbs';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';

interface ChildBreadcrumbsPublicProps {
    readonly isLarge: boolean;
    readonly sportChildId: string;
    readonly tournamentId?: string;
    readonly routeType: DisciplineRouteType;
    readonly disciplineName: string;
    readonly disciplineId: string;
    readonly lang: string;
    readonly eventId?: string;
    readonly isLocationType: boolean;
    readonly isCategoryType: boolean;
    readonly isLive?: boolean;
}

export type ChildBreadcrumbsProps = ChildBreadcrumbsPublicProps;

export const ChildBreadcrumbs: FC<ChildBreadcrumbsProps> = memo(function ChildBreadcrumbsComponent(
    {
        lang,
        isLarge,
        sportChildId,
        tournamentId,
        routeType,
        disciplineName,
        eventId,
        isCategoryType,
        isLocationType,
        disciplineId,
        isLive,
    }: ChildBreadcrumbsProps,
) {
    const params = useMemo(() => ({ sportId: sportChildId, lang }), [lang, sportChildId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<DisciplineModel>>('disciplines', params);
    const ref: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>();

    const { status }: RpcState<GetBreadcrumbsResponse<DisciplineModel>> = response;

    useEffect(() => {
        if (status === RpcStateStatus.Success && ref.current && !tournamentId) {
            ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
        }
    }, [status, tournamentId]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data }: RpcStateReady<GetBreadcrumbsResponse<DisciplineModel>> = response;

    const sportChildName: string | undefined = data.records[0]?.name || sportChildId;

    if (tournamentId) {
        return (
            <>
                <DisciplineAnchor
                    className={breadcrumbsBem('CurrentItem')}
                    title={sportChildName}
                    disciplineId={disciplineId}
                    disciplineName={disciplineName}
                    childId={sportChildId}
                    childName={sportChildName}
                    disciplineRouteType={routeType}
                    isLive={isLive}
                >
                    <span className={breadcrumbsBem('Text')}>{sportChildName}</span>
                </DisciplineAnchor>

                <span className={breadcrumbsBem({
                    element: 'Separator',
                    modifiers: 'Tournament',
                })}
                >&#47;
                </span>

                <TournamentBreadcrumbs
                    tournamentId={tournamentId}
                    isLarge={isLarge}
                    disciplineId={sportChildId}
                    disciplineName={sportChildName}
                    lang={lang}
                    eventId={eventId}
                    isLocationType={isLocationType}
                    isCategoryType={isCategoryType}
                />
            </>
        );
    }

    return (
        <span className={breadcrumbsBem(isLarge ? 'Text' : 'CurrentItem')} ref={ref}>{sportChildName}</span>
    );
});
